import { useLayoutEffect } from "react";
import * as THREE from "three";
import { OrbitControls, CSS2DRenderer } from "three-stdlib";
import { addDefaultScene } from "../sceneDefaults";
import { applyCoincidentLabelSpread } from "../coincidentLabelSpread";
import { clearViewerHandles, setViewerHandles } from "../../runtime/viewerHandles";
import { captureCameraHome } from "../../runtime/cameraHome";
import { deactivatePosPick } from "../../runtime/posPickState";
import { devLog } from "../scene/sceneConstants";
import { isLikelyTrackpadPanWheel, panCameraFromViewportDelta } from "../scene/sceneViewportInput";
import { safeTraverse } from "../scene/sceneObjectLookups";
import { reportRuntimeError } from "../../monitoring/runtimeErrors";
import { logger } from "../../utils/logger";
import { OPEN_RUNTIME_DIAGNOSTICS_EVENT } from "../../utils/browserCapabilityToasts";

function showRendererUnavailableMessage(mountElement) {
  const message = document.createElement("div");
  message.setAttribute("role", "alert");
  message.style.cssText = [
    "display:flex",
    "height:100%",
    "width:100%",
    "align-items:center",
    "justify-content:center",
    "padding:24px",
    "box-sizing:border-box",
    "background:#111827",
    "color:#fecaca",
    "font:14px system-ui, sans-serif",
    "text-align:center",
  ].join(";");
  message.textContent =
    "The 3D viewport could not start because WebGL is unavailable in this browser session.";
  mountElement.appendChild(message);
}

export function useThreeJsRenderer({
  mountRef,
  rendererRef,
  sceneRef,
  cameraRef,
  controlsRef,
  labelRendererRef,
  positionGizmoControllerRef,
  rotationGizmoControllerRef,
  scaleGizmoControllerRef,
  isOrbitingRef,
  preferPointerAfterOrbitRef,
  metricsActions,
  lastMetricsUpdate,
  jointLabelRefs,
  siteLabelsRef,
}) {
  // Initializes the 3D scene: cleans up previous renderers, creates new scene/camera,
  // sets up WebGL renderer with optimizations, and adds CSS2D renderer for labels
  useLayoutEffect(() => {
    const mountElement = mountRef.current;
    if (!mountElement) return;

    // Clean up any previous renderers/contexts
    while (mountElement.firstChild) {
      mountElement.removeChild(mountElement.firstChild);
    }

    // Dispose of previous renderer if it exists
    if (rendererRef.current) {
      rendererRef.current.forceContextLoss();
      rendererRef.current.dispose();
      rendererRef.current = null;
    }

    const scene = new THREE.Scene();
    sceneRef.current = scene;
    setViewerHandles({ scene });

    // Ensure position pick marker is hidden on scene initialization
    // (it will be created lazily when needed)
    deactivatePosPick();

    // Keep scene/camera/controls accessible via explicit runtime registry.

    const width = mountElement.clientWidth;
    const height = mountElement.clientHeight;
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.02, 1000);
    camera.position.set(0, 0.5, 2);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;
    setViewerHandles({ camera });

    // Create renderer with error handling
    try {
      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        powerPreference: "high-performance",
        failIfMajorPerformanceCaveat: false,
      });
      renderer.setSize(width, height);
      renderer.setPixelRatio(window.devicePixelRatio);
      // Mobile: default touch handling pans the document; keep drags for orbit/gizmo on canvas.
      renderer.domElement.style.touchAction = "none";
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.2;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      mountElement.appendChild(renderer.domElement);
      rendererRef.current = renderer;
      setViewerHandles({ renderer });
    } catch (error) {
      reportRuntimeError(
        error,
        {
          source: "three-webgl-renderer-create",
          viewportWidth: width,
          viewportHeight: height,
        },
        {
          toastKey: "three-webgl-renderer-create",
          toastMessage:
            "The 3D viewport could not start because WebGL is unavailable in this browser session.",
          toastDetail: {
            tone: "warning",
            action: {
              label: "Open runtime details",
              event: OPEN_RUNTIME_DIAGNOSTICS_EVENT,
            },
            durationMs: 12000,
          },
        }
      );
      showRendererUnavailableMessage(mountElement);
      return;
    }

    // Add CSS2D renderer for labels
    try {
      const labelRenderer = new CSS2DRenderer();
      labelRenderer.setSize(width, height);
      labelRenderer.domElement.style.position = "absolute";
      labelRenderer.domElement.style.top = "0px";
      labelRenderer.domElement.style.pointerEvents = "none";
      mountElement.appendChild(labelRenderer.domElement);
      labelRendererRef.current = labelRenderer;
    } catch (error) {
      logger.error("Failed to create label renderer:", error);
    }

    const controls = new OrbitControls(camera, rendererRef.current.domElement);
    controls.enableZoom = true;
    controls.zoomToCursor = true; // Enable zoom to cursor position
    const defaultMouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.PAN,
      RIGHT: null,
    };
    const shiftPanMouseButtons = {
      LEFT: THREE.MOUSE.PAN,
      MIDDLE: THREE.MOUSE.PAN,
      RIGHT: null,
    };
    controls.mouseButtons = defaultMouseButtons;
    controlsRef.current = controls;
    setViewerHandles({ controls });
    captureCameraHome();

    let shiftLeftPanActive = false;
    let shiftLeftPanDocumentTrackingAttached = false;
    const detachShiftLeftPanDocumentTracking = () => {
      if (!shiftLeftPanDocumentTrackingAttached) return;
      shiftLeftPanDocumentTrackingAttached = false;
      const doc = rendererRef.current?.domElement?.ownerDocument || document;
      doc.removeEventListener("pointerup", handleViewportPointerUpOrCancelCapture, true);
      doc.removeEventListener("pointercancel", handleViewportPointerUpOrCancelCapture, true);
    };
    const restoreDefaultMouseButtons = () => {
      if (!shiftLeftPanActive) return;
      shiftLeftPanActive = false;
      controls.mouseButtons = defaultMouseButtons;
      detachShiftLeftPanDocumentTracking();
    };
    const handleViewportPointerDownCapture = (event) => {
      if (event.button !== 0) return;
      if (event.shiftKey) {
        shiftLeftPanActive = true;
        controls.mouseButtons = shiftPanMouseButtons;
        rendererRef.current.domElement.style.cursor = "grabbing";
        if (!shiftLeftPanDocumentTrackingAttached) {
          shiftLeftPanDocumentTrackingAttached = true;
          const doc = rendererRef.current.domElement.ownerDocument;
          doc.addEventListener("pointerup", handleViewportPointerUpOrCancelCapture, true);
          doc.addEventListener("pointercancel", handleViewportPointerUpOrCancelCapture, true);
        }
      } else {
        restoreDefaultMouseButtons();
      }
    };
    const handleViewportPointerUpOrCancelCapture = (event) => {
      if (event.button !== 0 && event.type === "pointerup") return;
      restoreDefaultMouseButtons();
    };
    rendererRef.current.domElement.addEventListener(
      "pointerdown",
      handleViewportPointerDownCapture,
      true
    );
    rendererRef.current.domElement.addEventListener(
      "pointerup",
      handleViewportPointerUpOrCancelCapture,
      true
    );
    rendererRef.current.domElement.addEventListener(
      "pointercancel",
      handleViewportPointerUpOrCancelCapture,
      true
    );

    let lastTrackpadWheelAt = -Infinity;
    const handleViewportWheelCapture = (event) => {
      if (!isLikelyTrackpadPanWheel(event, lastTrackpadWheelAt)) return;

      lastTrackpadWheelAt = performance.now();
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      panCameraFromViewportDelta(
        camera,
        controls,
        rendererRef.current?.domElement,
        event.deltaX || 0,
        event.deltaY || 0
      );
    };
    rendererRef.current.domElement.addEventListener("wheel", handleViewportWheelCapture, {
      capture: true,
      passive: false,
    });

    controls.addEventListener("start", () => {
      isOrbitingRef.current = true;
    });
    controls.addEventListener("end", () => {
      isOrbitingRef.current = false;
      rendererRef.current.domElement.style.cursor = "default";
      if (preferPointerAfterOrbitRef.current) {
        rendererRef.current.domElement.style.cursor = "pointer";
        preferPointerAfterOrbitRef.current = false;
      }
    });

    // Add default scene environment (lights, floor, grid)
    addDefaultScene(scene);

    // Handle window resize
    const handleResize = () => {
      if (!mountElement || !cameraRef.current || !rendererRef.current) return;

      const width = mountElement.clientWidth;
      const height = mountElement.clientHeight;

      // Update camera
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();

      // Update renderers
      rendererRef.current.setSize(width, height);
      if (labelRendererRef.current) {
        labelRendererRef.current.setSize(width, height);
      }
    };

    // Add resize listener
    window.addEventListener("resize", handleResize);

    let animationFrameId = null;
    let renderLoopStopped = false;

    const animate = () => {
      if (renderLoopStopped || !rendererRef.current || !sceneRef.current || !cameraRef.current) {
        return;
      }

      try {
        controlsRef.current?.update();
        positionGizmoControllerRef.current?.frameUpdate?.();
        rotationGizmoControllerRef.current?.frameUpdate?.();
        scaleGizmoControllerRef.current?.frameUpdate?.();
        rendererRef.current.render(sceneRef.current, cameraRef.current);
        applyCoincidentLabelSpread(sceneRef.current, cameraRef.current);
        labelRendererRef.current?.render(sceneRef.current, cameraRef.current);

        // Count frames every render, but only update metrics display every 200ms
        metricsActions.incrementFrameCount();

        const now = performance.now();
        if (now - lastMetricsUpdate.current >= 200) {
          metricsActions.updateMetrics(rendererRef.current.info);
          lastMetricsUpdate.current = now;
        }

        animationFrameId = requestAnimationFrame(animate);
      } catch (error) {
        renderLoopStopped = true;
        reportRuntimeError(
          error,
          {
            source: "three-render-loop",
            hasScene: Boolean(sceneRef.current),
            hasCamera: Boolean(cameraRef.current),
            hasRenderer: Boolean(rendererRef.current),
          },
          {
            toastKey: "three-render-loop",
            toastMessage: "The scene renderer crashed. The editor is still running.",
          }
        );
      }
    };
    animationFrameId = requestAnimationFrame(animate);

    // Expose Three.js debugging interface (development only)
    if (typeof window !== "undefined" && import.meta.env.DEV) {
      window.threeDebug = {
        // Core Three.js objects
        scene: sceneRef.current,
        camera: cameraRef.current,
        renderer: rendererRef.current,
        controls: controlsRef.current,
        labelRenderer: labelRendererRef.current,

        // Three.js library for creating new objects
        THREE: THREE,
      };

      devLog("🚀 Three.js debugging interface available at window.threeDebug");
      devLog("📋 Direct access: threeDebug.scene, threeDebug.camera, threeDebug.renderer");
    }

    return () => {
      renderLoopStopped = true;
      if (animationFrameId != null) {
        cancelAnimationFrame(animationFrameId);
      }

      // Remove resize listener
      window.removeEventListener("resize", handleResize);
      rendererRef.current?.domElement.removeEventListener(
        "pointerdown",
        handleViewportPointerDownCapture,
        true
      );
      rendererRef.current?.domElement.removeEventListener(
        "pointerup",
        handleViewportPointerUpOrCancelCapture,
        true
      );
      rendererRef.current?.domElement.removeEventListener(
        "pointercancel",
        handleViewportPointerUpOrCancelCapture,
        true
      );
      detachShiftLeftPanDocumentTracking();
      rendererRef.current?.domElement.removeEventListener("wheel", handleViewportWheelCapture, {
        capture: true,
      });

      // Clean up renderers
      if (rendererRef.current) {
        rendererRef.current.forceContextLoss();
        rendererRef.current.dispose();
        rendererRef.current = null;
      }
      if (labelRendererRef.current) {
        labelRendererRef.current.domElement.remove();
        labelRendererRef.current = null;
      }

      // Clean up controls
      if (controlsRef.current) {
        controlsRef.current.dispose();
        controlsRef.current = null;
      }

      // Clean up scene
      if (sceneRef.current) {
        safeTraverse(sceneRef.current, (object) => {
          if (object.geometry) {
            object.geometry.dispose();
          }
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach((material) => material.dispose());
            } else {
              object.material.dispose();
            }
          }
        });
        sceneRef.current = null;
      }

      // Clean up camera
      cameraRef.current = null;
      clearViewerHandles();

      // Clean up refs
      jointLabelRefs.current = {};
      siteLabelsRef.current = {};

      if (mountElement) {
        mountElement.innerHTML = "";
      }
    };
  }, [
    cameraRef,
    controlsRef,
    isOrbitingRef,
    jointLabelRefs,
    labelRendererRef,
    lastMetricsUpdate,
    metricsActions,
    mountRef,
    positionGizmoControllerRef,
    preferPointerAfterOrbitRef,
    rendererRef,
    rotationGizmoControllerRef,
    scaleGizmoControllerRef,
    sceneRef,
    siteLabelsRef,
  ]);
}
